import { useNavigate } from 'react-router-dom';
import BottomNav from '@/components/feature/BottomNav';
import { quickEntries, categories, recommendRoutes, businessRecommendations } from '@/mocks/homeData';
import { useTrip } from '@/context/TripContext';
import { routeTemplates } from '@/mocks/routeData';

// 首页推荐路线卡片 → 路线模板天数映射
const routeCardMap: Record<string, { dayKey: string; crowd: string; prefs: string[] }> = {
  'route-1': { dayKey: '两天一夜', crowd: '亲子', prefs: ['拍照', '轻松'] },
  'route-2': { dayKey: '一日游', crowd: '情侣', prefs: ['拍照', '咖啡'] },
  'route-3': { dayKey: '两天一夜', crowd: '亲子', prefs: ['避暑', '户外'] },
  'route-4': { dayKey: '两天一夜', crowd: '朋友', prefs: ['户外', '拍照'] },
};

export default function Home() {
  const navigate = useNavigate();
  const { startRoute } = useTrip();

  const handleRecommendClick = (routeId: string) => {
    const mapping = routeCardMap[routeId];
    if (!mapping) {
      navigate('/create-route');
      return;
    }
    const route = routeTemplates[mapping.dayKey];
    if (route) {
      startRoute(route, {
        days: mapping.dayKey,
        crowd: mapping.crowd,
        preferences: mapping.prefs,
      });
      navigate('/route-result');
    } else {
      navigate('/create-route');
    }
  };

  return (
    <div className="min-h-screen bg-bg pb-20">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-bg">
        <div className="px-4 pt-3 pb-2">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-xl font-bold text-text-primary">玩安吉</h1>
            <button className="w-8 h-8 flex items-center justify-center cursor-pointer" aria-label="消息">
              <i className="ri-notification-3-line text-lg text-text-body" />
            </button>
          </div>
          <div
            className="flex items-center gap-2 px-3 py-2.5 bg-white rounded-lg border border-divider cursor-pointer"
            onClick={() => navigate('/explore')}
          >
            <i className="ri-search-line text-text-muted" />
            <span className="text-sm text-text-muted">搜云上草原、咖啡、亲子、雨天</span>
          </div>
        </div>
      </header>

      {/* Main CTA */}
      <section className="px-4 mt-2">
        <div
          className="relative overflow-hidden rounded-xl p-5 bg-bamboo cursor-pointer"
          onClick={() => navigate('/create-route')}
        >
          <div className="relative z-10">
            <p className="text-white/80 text-xs font-medium mb-1">不知道去哪玩？</p>
            <h2 className="text-white text-lg font-bold">一键生成路线</h2>
            <p className="text-white/70 text-xs mt-1">告诉我想玩几天，帮你安排</p>
          </div>
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center">
              <i className="ri-route-line text-white text-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Entries */}
      <section className="px-4 mt-4">
        <div className="grid grid-cols-4 gap-3">
          {quickEntries.map((entry) => (
            <button
              key={entry.label}
              className="flex flex-col items-center gap-1.5 py-3 px-1 cursor-pointer"
              onClick={() => navigate('/explore')}
            >
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${entry.color.split(' ')[0]}`}>
                <i className={`${entry.icon} text-lg ${entry.color.split(' ')[1]}`} />
              </div>
              <span className="text-xs font-medium text-text-body">{entry.label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="px-4 mt-2">
        <div className="bg-white rounded-xl p-4">
          <div className="grid grid-cols-4 gap-y-4 gap-x-2">
            {categories.map((cat) => (
              <button
                key={cat.label}
                className="flex flex-col items-center gap-1.5 cursor-pointer"
                onClick={() => navigate('/explore')}
              >
                <div className={`w-12 h-12 rounded-full ${cat.color} flex items-center justify-center`}>
                  <i className={`${cat.icon} text-bamboo text-lg`} />
                </div>
                <span className="text-xs font-medium text-text-body">{cat.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Recommended Routes */}
      <section className="px-4 mt-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-text-primary">推荐路线</h3>
          <button className="text-xs text-text-muted cursor-pointer flex items-center gap-0.5">
            更多 <i className="ri-arrow-right-s-line" />
          </button>
        </div>
        <div className="space-y-3">
          {recommendRoutes.map((route) => (
            <div
              key={route.id}
              className="bg-white rounded-xl overflow-hidden cursor-pointer"
              onClick={() => handleRecommendClick(route.id)}
            >
              <div className="relative h-40">
                <img src={route.image} alt={route.title} className="w-full h-full object-cover" />
                <span
                  className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-md text-xs font-medium text-white"
                  style={{ backgroundColor: 'rgba(46, 125, 50, 0.85)' }}
                >
                  {route.tag}
                </span>
              </div>
              <div className="p-3">
                <h4 className="text-base font-bold text-text-primary">{route.title}</h4>
                <p className="text-xs text-text-muted mt-0.5">{route.subtitle}</p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xs text-text-muted flex items-center gap-1">
                    <i className="ri-map-pin-line" /> {route.spots} 个地点
                  </span>
                  <span className="text-xs text-text-muted flex items-center gap-1">
                    <i className="ri-time-line" /> {route.duration}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Business Recommendations */}
      <section className="px-4 mt-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-text-primary">顺路推荐</h3>
        </div>
        <div className="space-y-3">
          {businessRecommendations.map((biz) => (
            <div
              key={biz.id}
              className="bg-white rounded-xl p-3 flex gap-3 cursor-pointer"
              onClick={() => {
                if (biz.placeId) {
                  navigate(`/place/${biz.placeId}`);
                } else {
                  navigate('/explore');
                }
              }}
            >
              <img
                src={biz.image}
                alt={biz.name}
                className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-stream font-medium">{biz.type}</p>
                <h4 className="text-sm font-bold text-text-primary mt-0.5">{biz.name}</h4>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-xs text-text-muted">{biz.area}</span>
                  <span className="text-xs text-bamboo bg-bamboo-pale px-1.5 py-0.5 rounded">{biz.tag}</span>
                </div>
                <p className="text-xs text-text-muted mt-1">{biz.price}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom Nav */}
      <BottomNav />
    </div>
  );
}